import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Board } from './components/Board';
import { CheckIcon, ChevronRightIcon, HelpIcon, LockIcon, MuteIcon, SettingsIcon, UnmuteIcon } from './components/Icons';
import { BaseSlideDemo, BallastArrowDemo, CairnTargetDemo, CrucibleMixerDemo, EmbersDetonateDemo, GaleWindDemo, KeepLockDemo, RivenHoleDemo } from './components/TutorialAnims';
import { WildlifeCameo } from './components/Wildlife';
import { detectPlatform, initEntitlement, purchaseFullUnlock, refreshEntitlementFromPlay } from './entitlements';

// Open a URL in the platform-appropriate browser surface. On iOS Capacitor
// this presents an SFSafariViewController as a sheet — the user reads with
// native iOS scrolling and momentum, taps "Done" to dismiss back to Tessera.
// Critical for legal pages (Privacy, Terms, Support) because the iOS
// WebView has scrollEnabled:false (set to prevent elastic-bounce on the
// game board), which kills body scroll inside the WebView. Browser.open
// sidesteps that entirely by using a separate iOS view controller.
//
// On Android Capacitor / TWA, Browser.open uses Chrome Custom Tabs (same
// pattern, equally clean). On plain web it falls back to window.open which
// behaves like target="_blank" — also fine since web doesn't have the
// scrollEnabled constraint. Unified API across all three rails.
function openExternal(url: string) {
  void Browser.open({ url, presentationStyle: 'popover' });
}
import { bootstrapAuth } from './auth';
import { track } from './firebase';
import { BONUS_LEVELS, WORLDS, worldByIndex, type WorldDef } from './levels';
import { sfx } from './audio';
import {
  mergeBests,
  mergeBonus,
  mergeProgress,
  readCloud,
  readLocal,
  readLocalBests,
  readLocalBonus,
  readLocalName,
  sanitizePlayerName,
  writeCloud,
  writeLocal,
  writeLocalBests,
  writeLocalBonus,
  writeLocalName,
  type BonusFound,
  type LevelBests,
  type WorldProgress,
} from './progress';
import type { LevelDef } from '@tessera/engine';

type View =
  | { kind: 'loading' }
  | { kind: 'picker' }
  | { kind: 'playing'; level: LevelDef }
  // When we're playing a bonus level, we remember the "returnTo" level so that
  // after the bonus clears (or is declined) the player lands back in the main
  // progression where they were. Bonus levels themselves are never on the
  // picker — they're ephemeral interstitials.
  | { kind: 'bonus-playing'; level: LevelDef; returnTo: LevelDef; returnNext: LevelDef | null }
  | { kind: 'bonus-won'; level: LevelDef; moves: number; returnTo: LevelDef; returnNext: LevelDef | null }
  | { kind: 'won'; level: LevelDef; moves: number; bonusOffer: LevelDef | null; next: LevelDef | null }
  | { kind: 'lost'; level: LevelDef; moves: number }
  | { kind: 'paywall'; reason: 'finished-free' | 'locked-world' };

const TUTORIAL_KEY = 'tessera.tutorialSeen.v1';
const THEME_KEY = 'tessera.theme.v1';

// Per-world mechanic tutorial localStorage key prefix.
// E.g., key 'tessera.tutorialSeen.world.v1.11' → user has seen W11 Keep tutorial.
// Suffix is the world index. Per-world keys so adding a tutorial for a new
// world doesn't reset others.
const WORLD_TUTORIAL_KEY_PREFIX = 'tessera.tutorialSeen.world.v1.';

// Per-world mechanic tutorials. Auto-shown on first entry to a level in
// each world (W11+), gated by the localStorage key per world. Players can
// also reopen via the existing "How to play" button — a future polish
// would extend that button to show the per-world tutorial when in a
// mechanic-rich world; for v0.11.x the tutorial fires once on first entry
// and is then dismissed forever (per device, since it's localStorage).
//
// Each entry: title (the world's name + dash + mechanic), body (3 lines
// teaching the mechanic, terse, declarative), tip (one-line strategic
// hint). All copy follows Quiet Paper voice — no exclamation, no chatty
// prose, no marketing-speak. Direct teaching.
//
// Worlds without an entry don't show a mechanic tutorial. W1-W10 use the
// general first-launch tutorial only; the difficulty curve there is
// communicated by the worlds themselves, not separate explainers.
interface WorldTutorialSpec {
  title: string;
  body: string[];
  tip?: string;
}
const WORLD_TUTORIALS: Record<number, WorldTutorialSpec | undefined> = {
  11: {
    title: 'World 11 — Keep',
    body: [
      'Locked tiles show a small padlock. They cannot be moved or merged directly.',
      'To unlock: build a charged tile of the same color (merge three normals), then move that charged tile adjacent to the locked tile. The lock breaks.',
      'The unlocked tile becomes a normal of its color, and can merge in chains.',
    ],
    tip: 'Charged tiles are your keys. Build one per locked color.',
  },
  12: {
    title: 'World 12 — Vault',
    body: [
      'Same mechanic as Keep: a charged tile of the matching color unlocks an adjacent lock.',
      'But here, every color has two locked tiles, not one.',
      'You will often need to chain unlocks — open one tile to set up access to the next.',
    ],
    tip: 'Plan two moves ahead per color. Order matters.',
  },
  13: {
    title: 'World 13 — Crucible',
    body: [
      'Mixer tiles are gray with three small dots. They start colorless.',
      'When a colored tile slides into a mixer (the mixer was the blocker), the mixer takes that tile’s color permanently.',
      'After the claim, the former mixer is a normal tile and merges like any other.',
    ],
    tip: 'You decide which color claims each mixer. Choose carefully.',
  },
  14: {
    title: 'World 14 — Ballast',
    body: [
      'Some cells have small direction arrows drawn into them.',
      'A tile that lands on an arrow cell automatically slides again in the arrow’s direction.',
      'This counts as one move — but the tile can travel much further than your single slide alone.',
    ],
    tip: 'Read the arrows before you commit. Look for cascade chains.',
  },
  15: {
    title: 'World 15 — Cairn',
    body: [
      'Some cells have a colored marker — those are targets.',
      'You win when each marked cell holds a tile of its required color, regardless of what else is on the board.',
      'You do NOT need to clear the rest. Sometimes clearing is wrong — you might consume the tile that was supposed to land on a target.',
    ],
    tip: 'Plan from the targets backward. What needs to be where, and how does it get there?',
  },
  16: {
    title: 'World 16 — Riven',
    body: [
      'Some cells are missing. They appear as gaps in the board.',
      'Tiles cannot land on a missing cell. A slide stops at the cell just before any gap, the same way it stops at the board edge.',
      'Rings, corridors, and crescents change which slides are possible. Two tiles that look adjacent on screen may be separated by a hole and unable to merge.',
    ],
    tip: 'The geometry is the puzzle. Read the shape before you tap.',
  },
  17: {
    title: 'World 17 — Gale',
    body: [
      'Some cells have a wind glyph — a curved arrow drawn into the cell.',
      'When a tile lands on the cell the wind points at, the wind nudges the tile one cell further in the wind’s direction.',
      'The whole travel is one move. Wind extends a slide by a single cell — sometimes into a chain you couldn’t reach without it.',
    ],
    tip: 'Wind cells are stoppers turned into pushers. Read the arrow before you slide.',
  },
  18: {
    title: 'World 18 — Embers',
    body: [
      'Reactive tiles glow with a faint pulsing outline. They look almost normal until they merge.',
      'When a reactive tile participates in a merge, it detonates. Every adjacent normal, locked, or mixer tile is removed in the same step.',
      'Charged tiles and other reactive tiles are spared by the detonation — your build-up stays intact.',
    ],
    tip: 'A reactive merge is a force-multiplier and a force-destroyer at once. Watch what you’re willing to lose.',
  },
  19: {
    title: 'World 19 — Cipher',
    body: [
      'No new mechanic. These levels combine rules you already know — gravity arrows, target markers, wind cells, reactive tiles, and the geometry from earlier worlds.',
      'Each level uses two of those mechanics together. Watch for sequences where one enables another: gravity that bumps a reactive into a chain, wind that nudges a tile onto a target.',
      'This is the climax. Everything you have learned, on one board.',
    ],
    tip: 'Read both mechanics before you slide. The trick is usually how they interact, not either one alone.',
  },
};

type ThemeChoice = 'system' | 'light' | 'dark';

function readThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* ignore */ }
  return 'system';
}

function writeThemeChoice(t: ThemeChoice) {
  try {
    if (t === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, t);
  } catch { /* ignore */ }
}

function applyThemeChoice(t: ThemeChoice) {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
}

// Free-tier scope. Worlds 1-3 (Threshold, Ember, Tide) — 30 levels total
// — are fully playable without purchase. These cover the d=1 to d=6
// difficulty band: learn the slide, introduce colors, build intuition
// for branching. Everything beyond (W4 Ashwood onward) is behind the
// paywall. v0.8.1 scope: 30 free / 70 paid, one unlock at $0.99.
const FREE_WORLD_CAP = 3;
const UNLOCK_PRICE_DISPLAY = '$0.99';

// Bonus-trigger rate after a clean clear. One-in-four feels "rare enough to
// be a surprise, frequent enough to matter" in playtests of similar games.
// Dropped to one-in-three in paid worlds (see bonusChanceFor) so paying
// players feel the game rewarding them more often — this is a pure tuning
// dial, safe to adjust without reshaping logic.
const BONUS_RATE_FREE = 0.25;
const BONUS_RATE_PAID = 0.33;

// Under-budget slack required for a "clean clear" — the gate that bonus
// triggers check. Tied to the hint rule: hints also disqualify. Two moves
// under budget means the player actually had room to spare, not that they
// squeaked through on the last move.
const CLEAN_CLEAR_SLACK = 2;

// TESTING FLAG — DISABLED in v0.18.0 for the Android Closed Testing restructure.
//
// History (preserved for context — this constant has been a recurring
// fault line and the lessons matter for any future revival):
//   v0.11.4 — `!Capacitor.isNativePlatform()` (test-mode true on Android TWA
//             + plain web, false on iOS Capacitor). Apple rejected build 12
//             on Guideline 2.1(b) — "all levels unlocked without showing
//             the payment sheet."
//   v0.11.6 — panic-fix: hardcoded false. Stuck through builds 13-15.
//   v0.11.9 — restored as `!isIosCapacitorRuntime()` after forensic analysis
//             showed the actual cause of the 2.1(b) rejection was a stale-
//             bundle issue (pnpm --filter @tessera/web build no-op'd
//             because @tessera/engine's compiled .d.ts hadn't refreshed),
//             not the platform check. Android testers had god-mode access
//             to all content from v0.11.9 through v0.17.0.
//   v0.18.0 — DISABLED ENTIRELY. The Android Closed Testing restructure
//             requires testers to exercise the actual production paywall
//             flow (hit W4+ paywall → unlock → progressive content release
//             across the 14-day window), so the blanket god-mode flag is
//             replaced by a per-tester unlock code (TESSERA-OPEN, see
//             applyTesterCodeUnlock in entitlements.ts) wired into a new
//             "I'm a tester" button on the paywall. This mirrors the iOS
//             production experience and produces a clearer engagement
//             signal for Google Play's production-access review.
//
// The constant is RETAINED (not deleted) because maxLevelFor and
// worldUnlocked still reference it; flipping to true is a single-line
// change if a future emergency requires re-enabling blanket god-mode for
// a specific debugging session. Production code paths must NEVER ship
// with this true — Apple's 2.1(b) tripwire is still there and the
// Android paywall enforcement also depends on it.
//
// The isIosCapacitorRuntime() helper was removed as dead code in v0.18.0.
// If platform-aware testing ever returns, restore the function and consult
// the v0.11.9 implementation in git history.
const TEST_MODE_UNLOCK_ALL = false;

function maxLevelFor(progress: WorldProgress, worldIndex: number, fullUnlock: boolean): number {
  // TEST_MODE blasts every level open — used for development QA only.
  if (TEST_MODE_UNLOCK_ALL) {
    const w = WORLDS.find((x) => x.index === worldIndex);
    if (w && w.levels.length > 0) {
      return w.levels.length + 1;
    }
  }
  // Apple Guideline 2.1(b) — App Completeness: a non-consumable IAP that
  // promises "all 100 levels" must deliver immediate access to all 100
  // levels, not gated access "as you progress." Build 10 (April 2026) was
  // rejected for keeping within-world sequential gating after purchase;
  // the reviewer interpreted locked-after-purchase levels as the IAP not
  // delivering. Build 11 fix: paid users get full unlock — every world,
  // every level, immediately accessible. Difficulty curve is communicated
  // via per-world theming + ordering, not enforced by gates.
  //
  // This matches the premium-puzzle convention: Hexcells, Monument Valley,
  // and similar all unlock all content on purchase; within-pack ordering
  // is suggestive (the picker lists levels in difficulty order), not
  // mandatory. Players who want the curated curve play in order naturally.
  if (fullUnlock) {
    const w = WORLDS.find((x) => x.index === worldIndex);
    if (w && w.levels.length > 0) {
      return w.levels.length + 1;
    }
  }
  // v0.18.1 (2026-05-28): free worlds (W1-W3) now fully open. Previously
  // sequential within each free world ("guided tour" philosophy from the
  // pre-launch era). Relaxed here for two reasons:
  //
  // (1) Closed-testing experience — testers should be able to sample any
  //     difficulty band immediately, not grind L1 → L2 → ... before they
  //     can stress-test (say) a W3 puzzle. Broader sampling = broader
  //     engagement signal for Google Play's production-access review.
  //
  // (2) Marketing-promise honesty — the App Store / Play Store listing
  //     says "30 levels free." A fresh user landing on W1L1-only with
  //     "L2 locked" tooltips reads that as bait-and-switch. Opening all
  //     30 free levels from first launch makes the promise literal and
  //     also makes the W3-to-W4 paywall transition feel like a clean
  //     "you've finished the free tier" gate rather than "the unlocks
  //     just stopped appearing."
  //
  // Within-world sequential gating is preserved for PAID worlds via the
  // fullUnlock branch above — purchasers still get the full ride, just
  // not enforced ordering. And the W4+ paywall enforcement is unchanged.
  if (worldIndex <= FREE_WORLD_CAP) {
    const w = WORLDS.find((x) => x.index === worldIndex);
    if (w && w.levels.length > 0) {
      return w.levels.length + 1;
    }
    return progress[worldIndex] ?? 1;
  }
  return 1;
}

function worldUnlocked(progress: WorldProgress, worldIndex: number, fullUnlock: boolean): boolean {
  if (worldIndex <= 1) return true;
  if (TEST_MODE_UNLOCK_ALL) {
    const w = WORLDS.find((x) => x.index === worldIndex);
    if (w && w.levels.length > 0) return true;
  }
  // Free worlds (W1-W3): all immediately accessible at install. Matches the
  // App Store description's promise of "thirty levels are free" — without
  // this rule, only W1's 10 levels are reachable at first launch, and
  // players (and Apple's reviewer) would reasonably ask "where are the
  // other 20 free levels?" That's a Guideline 2.3.1 (Accurate Metadata)
  // risk in addition to a UX problem. With this rule, all 30 free levels
  // are visible-and-tappable from the start; sequential progression
  // remains WITHIN each free world (must clear W1 L1 before W1 L2, etc.,
  // see maxLevelFor) so the curated difficulty curve is preserved per-
  // world, just not cross-world for the free tier.
  //
  // Paid users: every world (free + paid) accessible after purchase.
  // Within-world sequential gating still applies (must clear L1 before L2
  // in any world), but world-to-world traversal is unrestricted — paid
  // users can jump to W7 if they want, sample the climax, come back to
  // W4. Apple Guideline 2.1(b) compliant per build 11+ fix.
  //
  // Why the original "sequential everywhere even for paid" design was
  // wrong: the Monument Valley / Hexcells / Mini Metro pattern (within-
  // AND between-world sequential) made sense because those games are
  // fully paid from L1 — no freemium gate to confuse with the progression
  // gate. Tessera has both; conflating led to the 2026-04-29 rejection.
  if (worldIndex <= FREE_WORLD_CAP) return true;
  if (fullUnlock) return true;
  const prev = worldByIndex(worldIndex - 1);
  const prevMax = maxLevelFor(progress, prev.index, fullUnlock);
  return prevMax > prev.levels.length;
}

function isFreeWorld(worldIndex: number): boolean {
  return worldIndex <= FREE_WORLD_CAP;
}

function applyAccent(hex: string) {
  // Writes to --accent-base. The --accent var is derived from it in CSS,
  // so dark mode can lighten the world accent without the JS needing to
  // know anything about the current theme.
  document.documentElement.style.setProperty('--accent-base', hex);
}

// Picks a bonus level the player hasn't cleared yet. Once all 20 are found,
// falls back to a uniform random from the pool — the pearlescent reveal still
// feels rewarding even when it's a replay, especially once we track "found"
// as a collectible. Uses Math.random; bonus selection is not deterministic
// and intentionally non-repeatable — a player who reloads shouldn't be able
// to re-roll for a specific bonus.
function pickBonusLevel(found: BonusFound): LevelDef | null {
  if (BONUS_LEVELS.length === 0) return null;
  const unfound = BONUS_LEVELS.filter((l) => !found[l.id]);
  const pool = unfound.length > 0 ? unfound : BONUS_LEVELS;
  const idx = Math.floor(Math.random() * pool.length);
  return pool[idx] ?? null;
}

export function App() {
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [uid, setUid] = useState<string | null>(null);
  const [moves, setMoves] = useState(0);
  // W15 Cairn: count of currently-satisfied target cells. Updated via
  // Board's onTargetsChanged callback on mount and after every slide for
  // target-objective levels. Stays at 0 for 'clear' worlds (Board never
  // calls the callback for them, and HUD code only references this value
  // when objective.kind === 'target' so a stale 0 from a prior level is
  // immediately overwritten when the next target level mounts).
  const [satisfiedTargets, setSatisfiedTargets] = useState(0);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  // Per-world mechanic tutorial state. When non-null, the modal is open
  // for that world's mechanic. pendingLevel holds the level the player
  // tapped — startLevel staged it pending dismissal of the tutorial, and
  // dismissMechanicTutorial then continues to the level.
  const [mechanicTutorialWorld, setMechanicTutorialWorld] = useState<number | null>(null);
  const [pendingLevel, setPendingLevel] = useState<LevelDef | null>(null);
  const [progress, setProgress] = useState<WorldProgress>({});
  const [bests, setBests] = useState<LevelBests>({});
  const [bonusFound, setBonusFound] = useState<BonusFound>({});
  const [selectedWorld, setSelectedWorld] = useState(1);
  const [muted, setMuted] = useState(() => sfx.isMuted());
  const [attempt, setAttempt] = useState(0);
  // Player display name — retained per-device in localStorage, NOT cloud-synced.
  // null means "never set" and triggers the welcome modal on first run.
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Full-game entitlement. True when the user has purchased via Play Billing
  // (future: Stripe on web, StoreKit on iOS). Hydrated synchronously from
  // localStorage for no-flash gating; the async DGAPI refresh below can flip
  // it on in the rare case Play knows about a purchase we don't yet (cross-
  // device unlock, fresh install restoring ownership).
  const [fullUnlock, setFullUnlock] = useState<boolean>(() => initEntitlement((live) => setFullUnlock(live)));
  // UI state for the paywall's purchase button — 'idle' shows the price,
  // 'purchasing' disables the button while the platform store UI is open,
  // and the other states drive the post-purchase toast copy.
  const [purchaseState, setPurchaseState] = useState<'idle' | 'purchasing' | 'success' | 'cancelled' | 'failed' | 'unsupported'>('idle');
  // Diagnostic error from the last purchase attempt. Surfaced on the paywall
  // under the "Couldn't complete the purchase" message in tiny muted text
  // so we can debug RevenueCat / Play Billing config issues during launch
  // testing. SAFE TO LEAVE IN PRODUCTION — real-end-user errors are concise
  // ("Network error", "Invalid product id"), and we only render it when a
  // purchase has actually failed (idle / success / cancelled paths don't
  // show it). Strip later if the verbose form bothers users post-launch.
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  // Purchase platform — drives paywall copy (App Store vs. Play Store
  // wording on the disclaimer + error states). Default 'web' so SSR /
  // first paint doesn't show iOS copy on Android. Detection is async
  // because it dynamic-imports @capacitor/core, but the value never
  // flips after first resolution.
  const [purchasePlatform, setPurchasePlatform] = useState<'ios-capacitor' | 'web'>('web');
  useEffect(() => {
    let cancelled = false;
    detectPlatform()
      .then((p) => { if (!cancelled) setPurchasePlatform(p); })
      .catch(() => { /* default 'web' is fine */ });
    return () => { cancelled = true; };
  }, []);
  // v0.18.2: fire one analytics event per app load. This is the call that
  // lazily initializes GA4 (see firebase.ts) — and GA4's automatic
  // session_start on init is what actually populates daily-active-users.
  // Everything downstream (level_start, etc.) is engagement depth on top.
  useEffect(() => {
    track('app_open');
  }, []);
  // Theme override. 'system' (default) honors prefers-color-scheme; 'light'
  // or 'dark' force that theme regardless of system setting.
  const [themeChoice, setThemeChoice] = useState<ThemeChoice>(() => {
    const t = readThemeChoice();
    applyThemeChoice(t);
    return t;
  });
  const handleThemeChange = (t: ThemeChoice) => {
    setThemeChoice(t);
    writeThemeChoice(t);
    applyThemeChoice(t);
  };
  // Tracks whether the just-won run set a new personal best, so the won screen
  // can celebrate it with a distinct copy instead of the generic "Solved".
  const [newBest, setNewBest] = useState(false);
  // Tracks whether the just-won run used a hint. Hinted clears still count as
  // cleared (progress advances) but don't update the best-moves record, and
  // the won screen calls that out gently so the player isn't confused about
  // why their "best" didn't change.
  const [wonWithHint, setWonWithHint] = useState(false);

  const toggleMute = () => setMuted(sfx.toggleMuted());

  // Reset paywall button copy to 'idle' whenever the paywall view is re-
  // entered. Otherwise stale copy from a cancelled/failed attempt lingers
  // when the user navigates away and comes back.
  useEffect(() => {
    if (view.kind === 'paywall') {
      setPurchaseState('idle');
    }
  }, [view.kind]);

  // Re-query Play for the entitlement whenever the window regains focus —
  // catches cross-device purchases (user bought on another device, Play
  // remembers for this Google account, a tab-switch on return refreshes).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshEntitlementFromPlay().then((owned) => {
        if (owned) setFullUnlock(true);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    // Start with whatever's in localStorage — instant, always correct-or-newer.
    const local = readLocal();
    setProgress(local);
    setBests(readLocalBests());
    setBonusFound(readLocalBonus());
    const savedName = readLocalName();
    setPlayerName(savedName);
    // On a fresh install: welcome modal first (collect the name), then after
    // they dismiss it we open the tutorial. If name already exists (returning
    // player), we open the tutorial directly only when it hasn't been seen
    // yet. This keeps fresh runs feeling guided, not modal-spammed.
    let tutorialPending = false;
    try {
      tutorialPending = !localStorage.getItem(TUTORIAL_KEY);
    } catch { tutorialPending = true; }
    if (savedName === null) {
      setWelcomeOpen(true);
      // Tutorial is deferred; dismissWelcome opens it if tutorialPending.
    } else if (tutorialPending) {
      setTutorialOpen(true);
    }

    // Then auth + cloud merge. For worlds: per-world max(local, cloud) — no
    // data ever regresses. For bests: per-level min(local, cloud) — lower is
    // better. Both flow back to both sides if either side has new info.
    bootstrapAuth()
      .then(async (u) => {
        setUid(u.uid);
        setView({ kind: 'picker' });
        const localBestsNow = readLocalBests();
        const localBonusNow = readLocalBonus();
        const cloud = await readCloud(u.uid);
        const mergedProgress = mergeProgress(local, cloud.worlds);
        const mergedBests = mergeBests(localBestsNow, cloud.bests);
        const mergedBonus = mergeBonus(localBonusNow, cloud.bonus);
        // Only update state/writes if something actually changed — avoids
        // a pointless render and a pointless cloud write on every boot.
        const progressChangedLocal = JSON.stringify(mergedProgress) !== JSON.stringify(local);
        const progressChangedCloud = JSON.stringify(mergedProgress) !== JSON.stringify(cloud.worlds);
        const bestsChangedLocal = JSON.stringify(mergedBests) !== JSON.stringify(localBestsNow);
        const bestsChangedCloud = JSON.stringify(mergedBests) !== JSON.stringify(cloud.bests);
        const bonusChangedLocal = JSON.stringify(mergedBonus) !== JSON.stringify(localBonusNow);
        const bonusChangedCloud = JSON.stringify(mergedBonus) !== JSON.stringify(cloud.bonus);
        if (progressChangedLocal) {
          setProgress(mergedProgress);
          writeLocal(mergedProgress);
        }
        if (bestsChangedLocal) {
          setBests(mergedBests);
          writeLocalBests(mergedBests);
        }
        if (bonusChangedLocal) {
          setBonusFound(mergedBonus);
          writeLocalBonus(mergedBonus);
        }
        if (progressChangedCloud || bestsChangedCloud || bonusChangedCloud) {
          void writeCloud(u.uid, mergedProgress, mergedBests, mergedBonus);
        }
      })
      .catch((e) => { console.error('auth failed', e); setView({ kind: 'picker' }); });
  }, []);

  // Keep accent in sync with the world you're looking at / playing.
  // Bonus views switch the root body class so the pearlescent palette takes
  // over without us having to thread a prop everywhere; CSS handles the rest.
  // Per-world themed treatments (currently W6 prism) also use a body class
  // so CSS can key on it: .world-6 .board-wrap { ... }.
  useEffect(() => {
    const inBonus = view.kind === 'bonus-playing' || view.kind === 'bonus-won';
    // Clear any prior world-N class so switches don't stack.
    for (const c of [...document.body.classList]) {
      if (c.startsWith('world-')) document.body.classList.remove(c);
    }
    if (inBonus) {
      document.body.classList.add('in-bonus');
      // Pearlescent accent — warm but not loud, tuned to feel "different but
      // still Tessera" (see Quiet Paper art direction).
      applyAccent('#C9A063');
      return;
    }
    document.body.classList.remove('in-bonus');
    const wi =
      view.kind === 'playing' || view.kind === 'won' || view.kind === 'lost'
        ? view.level.world
        : selectedWorld;
    document.body.classList.add(`world-${wi}`);
    applyAccent(worldByIndex(wi).accent);
  }, [view, selectedWorld]);

  const dismissTutorial = () => {
    setTutorialOpen(false);
    try { localStorage.setItem(TUTORIAL_KEY, '1'); } catch { /* ignore */ }
  };

  // Called when the welcome modal submits a name (or skips). Persists the
  // sanitized name and, if the tutorial hasn't been seen yet, opens it next —
  // so the first-run flow is welcome → tutorial → picker, cleanly sequenced.
  const dismissWelcome = (rawName: string) => {
    const clean = sanitizePlayerName(rawName);
    const finalName = clean.length > 0 ? clean : 'Player';
    setPlayerName(finalName);
    writeLocalName(finalName);
    setWelcomeOpen(false);
    let tutorialSeen = false;
    try { tutorialSeen = localStorage.getItem(TUTORIAL_KEY) === '1'; } catch { /* default false */ }
    if (!tutorialSeen) setTutorialOpen(true);
  };

  // Settings actions. Name changes persist immediately (no Save button —
  // punitive on mobile). Reset-progress is destructive, gated behind a
  // confirm, and wipes BOTH local state and cloud state if we're signed in.
  const commitName = (rawName: string) => {
    const clean = sanitizePlayerName(rawName);
    const finalName = clean.length > 0 ? clean : 'Player';
    setPlayerName(finalName);
    writeLocalName(finalName);
  };
  const resetProgress = () => {
    if (!window.confirm('Reset all progress and best-moves? This cannot be undone.')) return;
    setProgress({});
    setBests({});
    setBonusFound({});
    writeLocal({});
    writeLocalBests({});
    writeLocalBonus({});
    if (uid) void writeCloud(uid, {}, {}, {});
    setSettingsOpen(false);
    setSelectedWorld(1);
  };

  const startLevel = (l: LevelDef) => {
    // Per-world mechanic tutorial gate — if this world has a mechanic
    // tutorial that hasn't been seen, stage the level and open the modal
    // first. Dismissal continues to the level (see dismissMechanicTutorial).
    const tut = WORLD_TUTORIALS[l.world];
    if (tut) {
      let seen = false;
      try {
        seen = localStorage.getItem(WORLD_TUTORIAL_KEY_PREFIX + l.world) === '1';
      } catch { /* default not-seen */ }
      if (!seen) {
        setPendingLevel(l);
        setMechanicTutorialWorld(l.world);
        return;
      }
    }
    // v0.18.2: engagement-depth signal — distinguishes "opened the app" from
    // "actually played a level". world/level params let us segment by world
    // (e.g. are testers reaching the W4 paywall, the dripped W16/W17, etc.).
    track('level_start', { world: l.world, level: l.id });
    setMoves(0);
    setSatisfiedTargets(0);
    // Bump attempt so Board remounts (its internal state keys off this via `key=`).
    setAttempt((n) => n + 1);
    setView({ kind: 'playing', level: l });
  };

  // v0.12.1: manual tutorial recall. Tester feedback was that the auto-fire
  // on first level entry didn't always stick — players who didn't absorb the
  // mechanic the first time had no way back to it without resetting progress.
  // Wired into a "?" button on the playing screen + picker (and inside the
  // mechanic tutorial itself, as a "show basics" link). Always opens the
  // most-relevant tutorial for the given world: mechanic if W11+ has one,
  // base "How to play" otherwise.
  const showTutorialFor = (worldIndex: number) => {
    const spec = WORLD_TUTORIALS[worldIndex];
    if (spec) {
      // Manual trigger: don't stage a pending level (we're not advancing
      // anything, just refreshing the player's understanding). dismiss
      // path's `pendingLevel ?? null` branch correctly no-ops on close.
      setMechanicTutorialWorld(worldIndex);
    } else {
      setTutorialOpen(true);
    }
  };

  const dismissMechanicTutorial = () => {
    const w = mechanicTutorialWorld;
    if (w !== null) {
      try { localStorage.setItem(WORLD_TUTORIAL_KEY_PREFIX + w, '1'); } catch { /* ignore */ }
    }
    setMechanicTutorialWorld(null);
    // Continue to the staged level if any.
    const l = pendingLevel;
    if (l) {
      setPendingLevel(null);
      setMoves(0);
      setSatisfiedTargets(0);
      setAttempt((n) => n + 1);
      setView({ kind: 'playing', level: l });
    }
  };

  const handleLost = (level: LevelDef, movesUsed: number) => {
    setView({ kind: 'lost', level, moves: movesUsed });
  };

  const handleSolved = (level: LevelDef, movesUsed: number, hintUsed: boolean) => {
    const unlockedNext = level.index + 1;
    const curMax = maxLevelFor(progress, level.world, fullUnlock);
    let nextProgress = progress;
    if (unlockedNext > curMax) {
      nextProgress = { ...progress, [level.world]: unlockedNext };
      setProgress(nextProgress);
      writeLocal(nextProgress);
    }
    // Best-moves: track the minimum move count per level id. First clear is
    // always a "new best" (no prior record); subsequent clears only update
    // when strictly lower. This is what drives the replay-for-par loop.
    //
    // Hinted clears are deliberately excluded — the hint reveals an optimal
    // first move, so letting it count would make "best" meaningless. Progress
    // still advances (it's still a clear), but the personal par stays clean.
    const prior = bests[level.id];
    const wouldBeNewBest = prior === undefined || movesUsed < prior;
    const isNewBest = wouldBeNewBest && !hintUsed;
    let nextBests = bests;
    if (isNewBest) {
      nextBests = { ...bests, [level.id]: movesUsed };
      setBests(nextBests);
      writeLocalBests(nextBests);
    }
    // Single cloud write for progress + bests + bonus (unchanged for bonus on
    // a non-bonus clear, but the write sends all three in one round trip).
    if (uid && (nextProgress !== progress || nextBests !== bests)) {
      void writeCloud(uid, nextProgress, nextBests, bonusFound);
    }
    setNewBest(isNewBest && prior !== undefined); // first clear isn't framed as "new best"
    setWonWithHint(hintUsed);

    // Bonus trigger: only on clean clears (no hint, under budget by the slack
    // threshold). Variable-ratio — never guaranteed even if the clear qualifies,
    // so the surprise stays a surprise. Rate is slightly higher in paid worlds
    // as a tangible reward for paying players. Offers a RANDOM unplayed bonus
    // first; once the player has cleared them all, we recycle least-recently-
    // wanted (any random from the pool).
    const budget = level.objective.kind === 'clear' ? level.objective.moveBudget : 0;
    const underBy = budget - movesUsed;
    const cleanClear = !hintUsed && underBy >= CLEAN_CLEAR_SLACK;
    const rate = isFreeWorld(level.world) ? BONUS_RATE_FREE : BONUS_RATE_PAID;
    const rolled = cleanClear && Math.random() < rate;
    const bonusOffer = rolled ? pickBonusLevel(bonusFound) : null;

    const next = nextLevelOf(level);
    setView({ kind: 'won', level, moves: movesUsed, bonusOffer, next });
  };

  // Bonus accept — jump into the bonus level, remembering where to return
  // once it's done (or declined). The Board remount is forced by bumping
  // attempt, same as a normal level start, so any prior bonus run's state
  // is cleared.
  const acceptBonus = (bonus: LevelDef, returnTo: LevelDef, returnNext: LevelDef | null) => {
    setMoves(0);
    setSatisfiedTargets(0);
    setAttempt((n) => n + 1);
    setView({ kind: 'bonus-playing', level: bonus, returnTo, returnNext });
  };

  // Called when the bonus itself is cleared. Marks it "found" (union — once
  // found, always found) and routes to the bonus-won screen which handles
  // the "back to main progression" button.
  const handleBonusSolved = (bonus: LevelDef, movesUsed: number, returnTo: LevelDef, returnNext: LevelDef | null) => {
    if (!bonusFound[bonus.id]) {
      const nextBonus: BonusFound = { ...bonusFound, [bonus.id]: true };
      setBonusFound(nextBonus);
      writeLocalBonus(nextBonus);
      if (uid) void writeCloud(uid, progress, bests, nextBonus);
    }
    setView({ kind: 'bonus-won', level: bonus, moves: movesUsed, returnTo, returnNext });
  };

  const handleBonusLost = (bonus: LevelDef, _movesUsed: number, returnTo: LevelDef, returnNext: LevelDef | null) => {
    // Bonus fails don't count against anything — treat a fail as "decline and
    // move on". Player is still rewarded with progress in the main line.
    void bonus;
    if (returnNext) {
      setSelectedWorld(returnNext.world);
      startLevel(returnNext);
    } else {
      void returnTo;
      setView({ kind: 'picker' });
    }
  };

  // Finds the next level in the same world, falling back to the first level
  // of the next world if that world is now unlocked AND inside the free tier.
  // Returning null signals "no next level" — caller may route to paywall if
  // the next world exists but is locked behind it.
  const nextLevelOf = (cur: LevelDef): LevelDef | null => {
    const world = worldByIndex(cur.world);
    const here = world.levels.find((l) => l.index === cur.index + 1);
    if (here) return here;
    // End of world — can we advance into the next one?
    const nextW = WORLDS.find((w) => w.index === cur.world + 1);
    if (!nextW) return null;
    // Paid-tier gate: non-purchased users stop at the free cliff (W3 → W4).
    // Purchased users flow through because they own the unlock.
    if (!isFreeWorld(nextW.index) && !fullUnlock) return null;
    const unlocked = nextW.levels[0] ?? null;
    const liveProgress = { ...progress, [cur.world]: Math.max(maxLevelFor(progress, cur.world, fullUnlock), cur.index + 1) };
    if (!worldUnlocked(liveProgress, nextW.index, fullUnlock)) return null;
    return unlocked;
  };

  // Did finishing this level walk us off the free cliff? Only relevant for
  // non-purchased users — purchased users flow through W3 → W4 seamlessly.
  const finishedFreeTier = (cur: LevelDef): boolean => {
    if (fullUnlock) return false;
    if (cur.world !== FREE_WORLD_CAP) return false;
    const world = worldByIndex(cur.world);
    if (cur.index !== world.levels.length) return false;
    const nextW = WORLDS.find((w) => w.index === cur.world + 1);
    return !!nextW;
  };

  if (view.kind === 'loading') {
    return <div className="app"><div className="header"><div className="title">Tessera</div></div><div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>Preparing…</div></div>;
  }

  if (view.kind === 'picker') {
    const world = worldByIndex(selectedWorld);
    const wMax = maxLevelFor(progress, selectedWorld, fullUnlock);
    // `wMax` is inflated by TEST_MODE_UNLOCK_ALL so we can playtest every
    // level; that's correct for *unlocking*. But "cleared" should reflect
    // what the player ACTUALLY beat — so the ✓ / "cleared" tag always
    // stories true state, even when test-mode is on. `actualMax` is the
    // honest progress index, same shape `maxLevelFor` returns but without
    // the test-mode override.
    const actualMax = progress[selectedWorld] ?? 1;
    return (
      <div className="app">
        <div className="header">
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <div className="title">Tessera</div>
            {playerName && (
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Hi, {playerName}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setTutorialOpen(true)} style={{ padding: '6px 12px', fontSize: 13 }}>How to play</button>
            <button className="icon-btn" aria-label="settings" title="Settings" onClick={() => setSettingsOpen(true)}>
              <SettingsIcon />
            </button>
          </div>
        </div>
        <WorldTabs
          worlds={WORLDS}
          selected={selectedWorld}
          progress={progress}
          fullUnlock={fullUnlock}
          onSelect={setSelectedWorld}
          onLockedPaidTap={() => setView({ kind: 'paywall', reason: 'locked-world' })}
        />
        {/* v0.12.1: world headline doubles as a tutorial recall. For W11+
            worlds with a mechanic tutorial it opens that tutorial (target
            cells, mixers, gravity, locks, etc.). For W1-W10 it falls back
            to the base "How to play." HelpIcon hint communicates clickability
            without overwhelming the line. Replaces the prior static text. */}
        <button
          type="button"
          className="link-btn"
          onClick={() => showTutorialFor(world.index)}
          aria-label="Show rules for this world"
          title="Show rules for this world"
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%' }}
        >
          <div className="objective" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>World {world.index} — {world.name} · <span style={{ color: 'var(--muted)' }}>{world.tagline}</span></span>
            <HelpIcon size={14} strokeWidth={1.6} style={{ color: 'var(--muted)', flexShrink: 0 }} />
          </div>
        </button>
        <div className="level-picker">
          {world.levels.map((l) => {
            const locked = l.index > wMax;
            const best = bests[l.id];
            // v0.12.0 fix: checkmark shows when the player has an actual
            // personal best for this specific level, not just when sequential
            // progression has passed it. Paid users open W4-W14 immediately
            // and play out of order; the previous `cleared = l.index <
            // actualMax` test missed this — a player who beat W6-L7 directly
            // would not see a checkmark on it because actualMax for W6 is
            // still 1 until sequential progression catches up. `bests[l.id]`
            // is set the moment a level is solved (handleSolved), so it's
            // the precise "I have personally cleared this" signal. Within
            // free worlds (W1-W3) where progression is sequential anyway,
            // the new check is identical to the old one.
            const cleared = best !== undefined;
            const lvlObj = l.objective;
            const budget = lvlObj.kind === 'clear' || lvlObj.kind === 'target' ? lvlObj.moveBudget : 0;
            // W15 Cairn: target levels show "Reach targets in N moves" /
            // "Best: K of N" — the personal-par line is identical (best
            // move count regardless of objective kind), only the un-played
            // descriptor needs to acknowledge the different goal.
            //
            // For cleared levels, swap "clear in N moves" for "your best: N" —
            // the unplayed budget is no longer the interesting number once you
            // beat it; the personal par is. Underscore the tie between "best"
            // and the move budget by stripping trailing noise.
            const isTarget = lvlObj.kind === 'target';
            const unplayedLine = isTarget
              ? `Reach targets in ${budget} moves`
              : `Clear in ${budget} moves`;
            const rightLine = locked
              ? 'Locked'
              : cleared && best !== undefined
                ? `Best: ${best} of ${budget}`
                : unplayedLine;
            return (
              <div
                key={l.id}
                className={`level-card${locked ? ' locked' : ''}`}
                onClick={() => {
                  if (!locked) startLevel(l);
                  else if (!fullUnlock) setView({ kind: 'paywall', reason: 'locked-world' });
                  // Locked + purchased = sequentially behind progression.
                  // No-op; the previous level's clear will unlock this one.
                }}
              >
                <div>
                  <div className="idx">Level {l.index}{cleared ? ' · cleared' : ''}</div>
                  <div className="name">{rightLine}</div>
                </div>
                <div className="card-icon" aria-hidden>
                  {locked
                    ? <LockIcon />
                    : cleared
                      ? <CheckIcon style={{ color: 'var(--accent)' }} />
                      : <ChevronRightIcon />}
                </div>
              </div>
            );
          })}
        </div>
        <div className="footer">
          <span>
            {WORLDS.reduce((n, w) => n + w.levels.length, 0)} levels · {WORLDS.length} worlds
            {BONUS_LEVELS.length > 0 && (() => {
              const foundCount = Object.keys(bonusFound).length;
              return foundCount > 0
                ? ` · ✦ ${foundCount}/${BONUS_LEVELS.length} bonus`
                : '';
            })()}
          </span>
          <span>v0.18.3{fullUnlock ? ' · full' : ''}</span>
        </div>
        {tutorialOpen && <Tutorial onClose={dismissTutorial} />}
        {mechanicTutorialWorld !== null && WORLD_TUTORIALS[mechanicTutorialWorld] && (
          <MechanicTutorial
            spec={WORLD_TUTORIALS[mechanicTutorialWorld]!}
            worldIndex={mechanicTutorialWorld}
            onClose={dismissMechanicTutorial}
            onShowBasics={() => {
              // Close the mechanic modal and open the basics. The dismiss
              // path also marks the mechanic tutorial as seen — that's the
              // correct behavior since the player did consume it (just
              // chose to layer the basics on top).
              dismissMechanicTutorial();
              setTutorialOpen(true);
            }}
          />
        )}
        {welcomeOpen && <WelcomeModal onDone={dismissWelcome} />}
        {settingsOpen && (
          <SettingsModal
            name={playerName}
            muted={muted}
            signedIn={!!uid}
            theme={themeChoice}
            fullUnlock={fullUnlock}
            onThemeChange={handleThemeChange}
            onCommitName={commitName}
            onToggleMute={toggleMute}
            onReset={resetProgress}
            onRestorePurchase={async () => {
              const owned = await refreshEntitlementFromPlay();
              if (owned) setFullUnlock(true);
              return owned;
            }}
            onClose={() => setSettingsOpen(false)}
          />
        )}
        {/* Wildlife cameo — per-world critter that ambles across every
            ~75s of idle time on the picker. Not during active play. */}
        <WildlifeCameo worldIndex={selectedWorld} />
      </div>
    );
  }

  if (view.kind === 'lost') {
    // W15 Cairn: budget exists on both 'clear' and 'target' objectives.
    // Body text varies — clear levels say "tiles remain"; target levels
    // say "the targets weren't all reached" (the actual failure mode).
    const lostObj = view.level.objective;
    const budget = lostObj.kind === 'clear' || lostObj.kind === 'target' ? lostObj.moveBudget : 0;
    const lostBody = lostObj.kind === 'target'
      ? `Level ${view.level.index} — you used all ${budget} moves but the targets weren't all reached. Every level here has a solution; try a different opening.`
      : `Level ${view.level.index} — you used all ${budget} moves but some tiles remain. Every level here has a solution; try a different opening.`;
    return (
      <div className="app">
        <div className="header">
          <button onClick={() => setView({ kind: 'picker' })}>← Levels</button>
          <div className="title">Tessera</div>
          <div style={{ width: 72 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 20 }}>
          <div className="t-display">Out of moves</div>
          <div className="t-body muted" style={{ textAlign: 'center', maxWidth: 340 }}>
            {lostBody}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="accent" onClick={() => startLevel(view.level)}>Try again</button>
            <button onClick={() => setView({ kind: 'picker' })}>Back to levels</button>
          </div>
        </div>
      </div>
    );
  }

  if (view.kind === 'won') {
    // W15 Cairn: budget exists on both objective kinds. The "moves of N"
    // line text reads identically for both so we don't need a target-
    // specific won screen — the achievement is the move count regardless
    // of whether the goal was emptying the board or hitting markers.
    const wonObj = view.level.objective;
    const budget = wonObj.kind === 'clear' || wonObj.kind === 'target' ? wonObj.moveBudget : 0;
    const next = view.next;
    const bonusOffer = view.bonusOffer;
    const finishedWorld = next && next.world !== view.level.world;
    const hitCliff = !next && finishedFreeTier(view.level);
    const priorBest = bests[view.level.id];
    // Show the prior best line whenever one exists and we didn't celebrate a
    // new-best (covers: tied best, beat budget but not best, and beat best
    // but with a hint — which doesn't count). The "New best" headline carries
    // the story on its own when it applies.
    const showPriorBest = priorBest !== undefined && !newBest;
    return (
      <div className="app">
        <div className="header">
          <button onClick={() => setView({ kind: 'picker' })}>← Levels</button>
          <div className="title">Tessera</div>
          <div style={{ width: 72 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 20 }}>
          <div className="t-display">
            {newBest
              ? (playerName ? `New best, ${playerName}` : 'New best')
              : (playerName ? `Nice, ${playerName}` : 'Solved')}
          </div>
          <div className="t-body muted">
            Level {view.level.index} · {view.moves} {view.moves === 1 ? 'move' : 'moves'} of {budget}
          </div>
          {showPriorBest && (
            <div className="t-ui muted">
              Your best: {priorBest} {priorBest === 1 ? 'move' : 'moves'}
            </div>
          )}
          {wonWithHint && (
            <div className="t-ui muted">
              Hint used — best not updated. Replay unhinted for a true record.
            </div>
          )}
          {view.moves < budget && (
            <div className="t-ui muted">
              Under par — {budget - view.moves} to spare
            </div>
          )}
          {finishedWorld && next && (
            <div className="t-ui muted" style={{ marginTop: 6 }}>
              World {worldByIndex(next.world).name} unlocked.
            </div>
          )}
          {bonusOffer && (
            <div className="bonus-offer">
              <div className="bonus-offer-shimmer" aria-hidden />
              <div style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                ✦ A bonus puzzle appeared
              </div>
              <div style={{ fontSize: 14, marginTop: 4, color: 'var(--charcoal)' }}>
                Clean clear earned you a little extra.
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => startLevel(view.level)}>Replay</button>
            {bonusOffer ? (
              <button
                className="accent bonus-btn"
                onClick={() => acceptBonus(bonusOffer, view.level, next)}
              >
                ✦ Take the bonus
              </button>
            ) : next ? (
              <button className="accent" onClick={() => { setSelectedWorld(next.world); startLevel(next); }}>
                {finishedWorld ? 'Enter next world ›' : 'Next level ›'}
              </button>
            ) : hitCliff ? (
              <button className="accent" onClick={() => setView({ kind: 'paywall', reason: 'finished-free' })}>
                What's next ›
              </button>
            ) : (
              <button className="accent" onClick={() => setView({ kind: 'picker' })}>Back to levels</button>
            )}
            {bonusOffer && next && (
              <button
                onClick={() => { setSelectedWorld(next.world); startLevel(next); }}
                style={{ fontSize: 13 }}
              >
                Skip
              </button>
            )}
          </div>
          {!next && !hitCliff && (
            <div className="muted" style={{ fontSize: 13, marginTop: 18, textAlign: 'center', maxWidth: 300 }}>
              That's every level we've built so far. More worlds on the way — thanks for playing early.
            </div>
          )}
        </div>
        {/* Won screen gets a cameo too — the world-specific critter
            celebrates with you, in its own quiet way. */}
        <WildlifeCameo worldIndex={view.level.world} checkMs={14000} />
      </div>
    );
  }

  if (view.kind === 'paywall') {
    const freeCount = WORLDS
      .filter((w) => isFreeWorld(w.index))
      .reduce((n, w) => n + w.levels.length, 0);
    const totalLevels = WORLDS.reduce((n, w) => n + w.levels.length, 0);
    return (
      <div className="app">
        <div className="header">
          <button onClick={() => setView({ kind: 'picker' })}>← Levels</button>
          <div className="title">Tessera</div>
          <div style={{ width: 72 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, padding: 24, textAlign: 'center' }}>
          <div className="t-display">
            {view.reason === 'finished-free' ? 'You cleared the free tier' : 'Tessera Full'}
          </div>
          <div className="t-body muted" style={{ maxWidth: 340 }}>
            {view.reason === 'finished-free'
              ? `You've solved all ${freeCount} free puzzles across three worlds. ${totalLevels - freeCount} more hand-curated levels across seven worlds are waiting — one unlock, no ads, yours forever.`
              : `Every world past the first three is part of Tessera Full. ${totalLevels - freeCount} more puzzles across seven worlds, one unlock.`}
          </div>
          <div style={{
            border: '1px solid color-mix(in oklab, var(--charcoal) 14%, transparent)',
            borderRadius: 14,
            padding: '18px 20px',
            background: 'color-mix(in oklab, var(--charcoal) 4%, var(--ivory))',
            maxWidth: 340,
            width: '100%',
            textAlign: 'left',
          }}>
            <div style={{ fontWeight: 500, fontSize: 16, marginBottom: 10 }}>Tessera Full · {UNLOCK_PRICE_DISPLAY}</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--charcoal)', lineHeight: 1.6 }}>
              <li>All {totalLevels} hand-curated levels · 10 worlds</li>
              <li>Every level provably solvable</li>
              <li>Offline play · cloud sync across devices</li>
              <li>One payment · no ads · no subscriptions</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => setView({ kind: 'picker' })} disabled={purchaseState === 'purchasing'}>Not now</button>
            <button
              className="accent"
              disabled={purchaseState === 'purchasing'}
              onClick={async () => {
                setPurchaseState('purchasing');
                setPurchaseError(null);
                try {
                  const result = await purchaseFullUnlock();
                  setPurchaseState(result.outcome);
                  setPurchaseError(result.error ?? null);
                  if (result.outcome === 'success') {
                    setFullUnlock(true);
                    setTimeout(() => setView({ kind: 'picker' }), 900);
                  }
                } catch (e) {
                  // Last-resort catch. purchaseFullUnlock already has its
                  // own try/catch, but unexpected runtime errors (e.g. a
                  // TypeError on malformed response) would otherwise leave
                  // the button stuck on 'purchasing'.
                  console.warn('paywall purchase outer catch', e);
                  setPurchaseState('failed');
                  setPurchaseError(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              {purchaseState === 'purchasing'
                ? (purchasePlatform === 'ios-capacitor' ? 'Opening App Store…' : 'Opening Play…')
                : purchaseState === 'success'
                  ? 'Unlocked — loading…'
                  : `Unlock Tessera Full — ${UNLOCK_PRICE_DISPLAY}`}
            </button>
          </div>
          {purchaseState === 'cancelled' && (
            <div className="t-ui muted" style={{ marginTop: 6 }}>Purchase cancelled. Your progress is still saved.</div>
          )}
          {purchaseState === 'failed' && (
            <div className="t-ui" style={{ marginTop: 6, color: 'var(--accent)' }}>
              Couldn't complete the purchase. Check your connection and try again.
              {purchaseError && (
                <div className="muted" style={{ fontSize: 10, marginTop: 4, opacity: 0.7, fontFamily: 'monospace', wordBreak: 'break-word' }}>
                  {purchaseError}
                </div>
              )}
            </div>
          )}
          {purchaseState === 'unsupported' && (
            <div className="t-ui muted" style={{ marginTop: 6, maxWidth: 320 }}>
              {purchasePlatform === 'ios-capacitor'
                ? "In-app purchase isn't available in this build."
                : "In-app purchase isn't available in this build. Install Tessera from Google Play to unlock the full game."}
            </div>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 8, maxWidth: 320 }}>
            {purchasePlatform === 'ios-capacitor'
              ? 'Purchases on iOS are handled by the App Store. One-time, non-consumable. Refund policy per Apple.'
              : 'Purchases on Android are handled by Google Play. One-time, non-consumable. Refund policy per Google Play.'}
          </div>
        </div>
      </div>
    );
  }

  if (view.kind === 'bonus-playing') {
    const bonusBudget = view.level.objective.kind === 'clear' ? view.level.objective.moveBudget : 0;
    // Abandon = return to the main-line "next" level if we have one, else the
    // picker. Never counts as a loss; bonus levels are pure upside.
    const abandon = () => handleBonusLost(view.level, moves, view.returnTo, view.returnNext);
    return (
      <div className="app bonus-shell">
        <div className="fizzle-overlay" aria-hidden />
        <div className="header">
          <button onClick={abandon}>← Skip</button>
          <div className="title">✦ Bonus</div>
          <div className="muted">{moves} / {bonusBudget}</div>
        </div>
        <div className="objective">A bonus puzzle. Same rules, shinier tiles.</div>
        <Board
          key={`${view.level.id}-${attempt}`}
          level={view.level}
          onMoves={setMoves}
          onSolved={(movesUsed) => handleBonusSolved(view.level, movesUsed, view.returnTo, view.returnNext)}
          onLost={(movesUsed) => handleBonusLost(view.level, movesUsed, view.returnTo, view.returnNext)}
        />
        <div className="footer">
          <button onClick={abandon}>Skip bonus</button>
          <span>✦ no stakes — just a treat</span>
        </div>
      </div>
    );
  }

  if (view.kind === 'bonus-won') {
    const totalFound = Object.keys(bonusFound).length;
    const total = BONUS_LEVELS.length;
    const onward = () => {
      if (view.returnNext) {
        setSelectedWorld(view.returnNext.world);
        startLevel(view.returnNext);
      } else {
        setView({ kind: 'picker' });
      }
    };
    return (
      <div className="app bonus-shell">
        <div className="header">
          <button onClick={() => setView({ kind: 'picker' })}>← Levels</button>
          <div className="title">✦ Bonus</div>
          <div style={{ width: 72 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 20 }}>
          <div className="t-display">
            {playerName ? `Lovely, ${playerName}` : 'Lovely'}
          </div>
          <div className="t-body muted">
            Bonus cleared in {view.moves} {view.moves === 1 ? 'move' : 'moves'}.
          </div>
          <div className="t-ui muted">
            ✦ {totalFound} of {total} bonus puzzles found
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="accent" onClick={onward}>
              {view.returnNext ? 'Back to the climb ›' : 'Back to levels ›'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // playing
  // W15 Cairn: budget exists on both 'clear' and 'target' objectives. Pull
  // it polymorphically so the HUD's "moves used / budget" line works the
  // same across both. Survival/pattern objectives (not yet shipped) would
  // need their own treatment.
  const obj = view.level.objective;
  const budget = obj.kind === 'clear' || obj.kind === 'target' ? obj.moveBudget : 0;
  // For target levels the objective text is "Reach the marked cells · K/N"
  // showing the live satisfied count. For clear levels it's the existing
  // "Empty the board in N moves." Wording deliberately avoids "in N moves"
  // for targets — the marker visual + counter already communicate budget;
  // re-stating it would be redundant.
  const targetCount = obj.kind === 'target' ? obj.targets.length : 0;
  const objectiveText = obj.kind === 'target'
    ? `Reach the marked cells · ${satisfiedTargets}/${targetCount}`
    : `Empty the board in ${budget} moves`;
  return (
    <div className="app">
      <div className="header">
        <button onClick={() => setView({ kind: 'picker' })}>← Levels</button>
        {/* v0.12.1: world name doubles as a tutorial-recall button. Tap to
            re-open the mechanic tutorial (W11+) or the base How-to-play
            (W1-W10). Tester feedback was that the auto-fire on first level
            entry didn't always stick; the help icon hint makes the action
            discoverable without adding visual clutter. */}
        <button
          className="link-btn"
          onClick={() => showTutorialFor(view.level.world)}
          aria-label="Show world rules"
          title="Show world rules"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--muted)', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
        >
          {worldByIndex(view.level.world).name}
          <HelpIcon size={14} strokeWidth={1.6} />
        </button>
        <div className="muted">{moves} / {budget}</div>
      </div>
      <div className="objective">{objectiveText}</div>
      <Board
        key={`${view.level.id}-${attempt}`}
        level={view.level}
        onMoves={setMoves}
        onTargetsChanged={setSatisfiedTargets}
        onSolved={(movesUsed, hintUsed) => handleSolved(view.level, movesUsed, hintUsed)}
        onLost={(movesUsed) => handleLost(view.level, movesUsed)}
      />
      {/* Level-as-sculpture: a large ghost-tinted tabular figure anchored
          below the board. The signature idea from the v0.3.x design memo —
          makes the level number the screenshot moment across 300 levels. */}
      <div className="level-sculpture" aria-hidden>{view.level.index}</div>
      <div className="footer">
        <button onClick={() => startLevel(view.level)}>Restart</button>
        <span>tap a tile, then a direction</span>
      </div>
      {/* v0.12.1 fix: tutorial modals also need to render on the playing
          view, not just the picker. The "?" header button writes to the
          same state used by the picker's auto-fire, but without these
          render lines the modal had nowhere to surface mid-level — tap
          on "?" was effectively a silent no-op. Same JSX as the picker
          render block; refactoring to a shared <TutorialModals/> would
          be cleaner long-term but a duplicated 18 lines is the minimum
          surgery for a bug fix that needs to ship today. */}
      {tutorialOpen && <Tutorial onClose={dismissTutorial} />}
      {mechanicTutorialWorld !== null && WORLD_TUTORIALS[mechanicTutorialWorld] && (
        <MechanicTutorial
          spec={WORLD_TUTORIALS[mechanicTutorialWorld]!}
          worldIndex={mechanicTutorialWorld}
          onClose={dismissMechanicTutorial}
          onShowBasics={() => {
            dismissMechanicTutorial();
            setTutorialOpen(true);
          }}
        />
      )}
    </div>
  );
}

interface WorldTabsProps {
  worlds: readonly WorldDef[];
  selected: number;
  progress: WorldProgress;
  fullUnlock: boolean;
  onSelect: (w: number) => void;
  onLockedPaidTap: () => void;
}

function WorldTabs({ worlds, selected, progress, fullUnlock, onSelect, onLockedPaidTap }: WorldTabsProps) {
  return (
    <div className="world-tabs">
      {worlds.map((w) => {
        const progressUnlocked = worldUnlocked(progress, w.index, fullUnlock);
        const free = w.index <= FREE_WORLD_CAP;
        // Paid worlds unlock when the player owns Tessera Full AND has
        // sequentially reached the world (cleared the previous one). Test
        // mode bypasses everything. Empty paid worlds still surface the
        // paywall so the pitch stays intact even before content ships.
        const ownedAndReachable = fullUnlock && w.levels.length > 0;
        const testUnlocked = TEST_MODE_UNLOCK_ALL && w.levels.length > 0;
        const unlocked = progressUnlocked && (free || testUnlocked || ownedAndReachable);
        const isSelected = w.index === selected;
        const done = (progress[w.index] ?? 1) > w.levels.length;
        // Paid worlds surface the paywall on tap — but only for users who
        // don't already own Tessera Full. Purchased users tapping a
        // sequentially-locked paid tab are just "not there yet" and get
        // a no-op (consistent with free-tier worlds tapped out of order).
        const handleClick = () => {
          if (unlocked) onSelect(w.index);
          else if (!free && !fullUnlock) onLockedPaidTap();
        };
        const title = unlocked
          ? w.tagline
          : !free
            ? 'Tessera Full — tap to learn more'
            : 'Clear the previous world to unlock';
        return (
          <button
            key={w.id}
            className={`world-tab${isSelected ? ' selected' : ''}${unlocked ? '' : ' locked'}`}
            style={{ borderColor: isSelected ? w.accent : undefined, color: isSelected ? w.accent : undefined }}
            disabled={!unlocked && free}
            onClick={handleClick}
            title={title}
          >
            <span className="world-dot" style={{ background: w.accent }} />
            <span>{w.name}</span>
            {done && <span className="world-check">✓</span>}
            {!unlocked && <span className="world-check">🔒</span>}
          </button>
        );
      })}
    </div>
  );
}

// First-run name capture. Single input, single primary button. The player
// can skip (empty submit) — we fall back to "Player" so every downstream
// reference has something to show. No cloud mirror: the name is per-device
// on purpose. Many puzzle players keep separate "serious" and "casual"
// installs, and this respects that by default.
function WelcomeModal({ onDone }: { onDone: (name: string) => void }) {
  const [name, setName] = useState('');
  const submit = () => onDone(name);
  return (
    <div className="modal-scrim">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Welcome to Tessera</div>
        <div style={{ fontSize: 14, color: 'var(--charcoal)', lineHeight: 1.55, marginBottom: 14 }}>
          What should we call you? This stays on this device — nothing is sent anywhere.
        </div>
        <input
          autoFocus
          type="text"
          value={name}
          placeholder="Your name"
          maxLength={20}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: 15,
            border: '1px solid #00000024',
            borderRadius: 10,
            background: '#FFFFFFA0',
            color: 'var(--charcoal)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Optional — leave blank to skip.
        </div>
        <button className="accent" onClick={submit} style={{ marginTop: 18, width: '100%' }}>
          Let's play
        </button>
      </div>
    </div>
  );
}

interface SettingsProps {
  name: string | null;
  muted: boolean;
  signedIn: boolean;
  theme: ThemeChoice;
  fullUnlock: boolean;
  onThemeChange: (t: ThemeChoice) => void;
  onCommitName: (name: string) => void;
  onToggleMute: () => void;
  onReset: () => void;
  // Apple Guideline 3.1.1 requires non-consumable IAPs to expose a visible
  // "Restore Purchases" affordance. Tessera's entitlements module already
  // queries the active rail (RevenueCat on iOS, DGAPI on Android TWA) at
  // boot and on visibility-change, so the button is technically redundant
  // for end users — but Apple's reviewer (and customer-support requests)
  // expects it to exist explicitly. Returns true if the rail confirmed
  // ownership, false otherwise.
  onRestorePurchase: () => Promise<boolean>;
  onClose: () => void;
}

// Settings is a pragmatic drawer: name, sound, theme, restore, reset, links.
// No tabs, no search. Name persists on blur rather than via a Save button —
// a Save button on a one-field form is the kind of friction that makes
// mobile players bounce.
function SettingsModal({ name, muted, signedIn, theme, fullUnlock, onThemeChange, onCommitName, onToggleMute, onReset, onRestorePurchase, onClose }: SettingsProps) {
  const [draft, setDraft] = useState(name ?? '');
  // Restore Purchase state — drives the button label / disable state.
  // 'idle' shows "Restore"; 'restoring' shows "Checking…" and disables;
  // 'restored' shows "✓ Restored" briefly; 'not-found' shows "No purchase
  // found" briefly; 'failed' shows "Try again". After a 'restored' /
  // 'not-found' / 'failed' state lands, a 3s timer auto-resets to 'idle'
  // so re-tap is possible. The fullUnlock prop drives the secondary line
  // ("Unlocked" vs "Not yet unlocked"); the button itself acts on either.
  const [restoreState, setRestoreState] = useState<'idle' | 'restoring' | 'restored' | 'not-found' | 'failed'>('idle');
  useEffect(() => {
    if (restoreState === 'idle' || restoreState === 'restoring') return;
    const t = window.setTimeout(() => setRestoreState('idle'), 3000);
    return () => window.clearTimeout(t);
  }, [restoreState]);
  const restoreLabel =
    restoreState === 'restoring' ? 'Checking…' :
    restoreState === 'restored' ? '✓ Restored' :
    restoreState === 'not-found' ? 'No purchase' :
    restoreState === 'failed' ? 'Try again' :
    'Restore';
  const commit = () => {
    if (draft !== (name ?? '')) onCommitName(draft);
  };
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Settings</div>

        <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>Name</label>
        <input
          type="text"
          value={draft}
          placeholder="Player"
          maxLength={20}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { commit(); (e.target as HTMLInputElement).blur(); } }}
          style={{
            width: '100%',
            padding: '9px 12px',
            fontSize: 15,
            border: '1px solid #00000024',
            borderRadius: 10,
            background: '#FFFFFFA0',
            color: 'var(--charcoal)',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 16,
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #00000012' }}>
          <div>
            <div style={{ fontSize: 14 }}>Sound & haptics</div>
            <div className="muted" style={{ fontSize: 12 }}>{muted ? 'Muted' : 'On'}</div>
          </div>
          <button className="icon-btn" aria-label={muted ? 'unmute' : 'mute'} onClick={onToggleMute}>
            {muted ? <MuteIcon /> : <UnmuteIcon />}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #00000012', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14 }}>Theme</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {theme === 'system' ? 'Follows device' : theme === 'light' ? 'Light' : 'Dark'}
            </div>
          </div>
          <div className="theme-picker" role="radiogroup" aria-label="Theme">
            {(['system', 'light', 'dark'] as const).map((opt) => (
              <button
                key={opt}
                role="radio"
                aria-checked={theme === opt}
                className={`theme-opt${theme === opt ? ' selected' : ''}`}
                onClick={() => onThemeChange(opt)}
              >
                {opt === 'system' ? 'Auto' : opt === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #00000012' }}>
          <div>
            <div style={{ fontSize: 14 }}>Cloud sync</div>
            <div className="muted" style={{ fontSize: 12 }}>{signedIn ? 'Signed in · progress syncs' : 'Offline · progress is local'}</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #00000012' }}>
          <div>
            <div style={{ fontSize: 14 }}>Tessera Full</div>
            <div className="muted" style={{ fontSize: 12 }}>{fullUnlock ? 'Unlocked' : 'Not yet unlocked'}</div>
          </div>
          <button
            onClick={async () => {
              setRestoreState('restoring');
              try {
                const owned = await onRestorePurchase();
                setRestoreState(owned ? 'restored' : 'not-found');
              } catch (e) {
                console.warn('restore purchase failed', e);
                setRestoreState('failed');
              }
            }}
            disabled={restoreState === 'restoring'}
            style={{ fontSize: 13 }}
            aria-label="Restore previous purchase"
          >
            {restoreLabel}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0 14px', borderTop: '1px solid #00000012' }}>
          <div>
            <div style={{ fontSize: 14 }}>Reset progress</div>
            <div className="muted" style={{ fontSize: 12 }}>Clears all best-moves and unlocks.</div>
          </div>
          <button onClick={onReset} style={{ fontSize: 13 }}>Reset</button>
        </div>

        {/* Legal links — Apple review requires easy in-app access. Open via
            Capacitor Browser plugin (SFSafariViewController on iOS, Chrome
            Custom Tabs on Android) so legal pages get native scrolling and a
            clean Done button. The href stays as a fully-qualified URL so
            right-click "open in new tab" still works on plain web for power
            users. Contact stays as mailto: — iOS Mail handles that scheme
            natively without needing a Browser sheet. */}
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', padding: '14px 0 0', fontSize: 12, color: 'var(--muted)' }}>
          <a
            href="https://puzzle-d583c.web.app/legal/privacy.html"
            onClick={(e) => { e.preventDefault(); openExternal('https://puzzle-d583c.web.app/legal/privacy.html'); }}
            style={{ color: 'inherit' }}
          >Privacy</a>
          <span aria-hidden>·</span>
          <a
            href="https://puzzle-d583c.web.app/legal/terms.html"
            onClick={(e) => { e.preventDefault(); openExternal('https://puzzle-d583c.web.app/legal/terms.html'); }}
            style={{ color: 'inherit' }}
          >Terms</a>
          <span aria-hidden>·</span>
          <a href="mailto:support@acgapps.org" style={{ color: 'inherit' }}>Contact</a>
        </div>

        <button className="accent" onClick={onClose} style={{ marginTop: 14, width: '100%' }}>Done</button>
      </div>
    </div>
  );
}

function Tutorial({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">How to play</div>
        {/* Animated demo: shows that ALL tiles in the path slide together
            on a single tap. This is the most-misunderstood mechanic from
            text alone — testers tried "tap a tile to swap" (Bejeweled-style)
            until they saw the row-slide in action. */}
        <BaseSlideDemo />
        <ol className="tut-list">
          <li><b>Slide tiles.</b> Tap a tile, then a direction. Every tile in the path slides — not just the one you tapped.</li>
          <li><b>Match three.</b> Bring 3 same‑colour tiles together and they merge into a single <em>charged</em> tile (with a ring).</li>
          <li><b>Clear with a chain.</b> A charged tile plus 2 more same‑colour tiles vanishes the whole group.</li>
          <li><b>Cascades.</b> One slide can trigger merges in other colours too — shifted tiles that land into same‑colour groups all resolve together.</li>
          <li><b>Pre‑made groups.</b> Some levels start with 3 same‑colour tiles already adjacent. Your first move will trigger their merge, even if you touch something else. Watch for a pulsing ring at level start — that's your heads‑up.</li>
          <li><b>Goal.</b> Empty the board within the move budget shown at top right.</li>
        </ol>
        <div className="tut-hint muted">
          Tip — every level here is solvable. No guessing.
          <br />
          <span style={{ fontSize: 11 }}>Keyboard: <kbd>Z</kbd> undo · <kbd>H</kbd> hint · <kbd>Esc</kbd> deselect</span>
        </div>
        <button className="accent" onClick={onClose} style={{ marginTop: 20, width: '100%' }}>Got it</button>
      </div>
    </div>
  );
}

// Per-world mechanic tutorial. Same modal aesthetic as the base Tutorial,
// but content is sourced from WORLD_TUTORIALS by world index. Auto-shown
// once per world on first level entry; dismissed → localStorage tracks
// "seen" → never auto-shows again on this device.
//
// `worldIndex` drives an optional inline animated demo. W12 deliberately
// reuses the W11 lock demo because the underlying mechanic is identical
// (just denser locks); a second copy of the same animation in two adjacent
// modals would feel redundant. World indexes without a registered demo
// fall through to text-only — same as before this commit.
function mechanicDemoFor(worldIndex: number): JSX.Element | null {
  switch (worldIndex) {
    case 11: return <KeepLockDemo />;
    case 12: return <KeepLockDemo />;
    case 13: return <CrucibleMixerDemo />;
    case 14: return <BallastArrowDemo />;
    case 15: return <CairnTargetDemo />;
    case 16: return <RivenHoleDemo />;
    case 17: return <GaleWindDemo />;
    case 18: return <EmbersDetonateDemo />;
    default: return null;
  }
}

function MechanicTutorial({ spec, worldIndex, onClose, onShowBasics }: { spec: WorldTutorialSpec; worldIndex: number; onClose: () => void; onShowBasics?: () => void }) {
  const demo = mechanicDemoFor(worldIndex);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{spec.title}</div>
        {demo}
        <div className="tut-list" style={{ paddingLeft: 0, listStyle: 'none' }}>
          {spec.body.map((line, i) => (
            <div key={i} style={{ margin: '8px 0', lineHeight: 1.5 }}>{line}</div>
          ))}
        </div>
        {spec.tip && (
          <div className="tut-hint muted">
            {spec.tip}
          </div>
        )}
        <button className="accent" onClick={onClose} style={{ marginTop: 20, width: '100%' }}>Got it</button>
        {/* v0.12.1: "Show basics" inline fallback. Players who tap the
            world's tutorial button but actually wanted the fundamentals
            ("how does a slide work again?") get a one-tap path back to
            the base How-to-play. Quiet Paper voice — small muted link,
            not a competing CTA. */}
        {onShowBasics && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={onShowBasics}
              style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 4 }}
            >
              Show basics
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
